import React from 'react';
import { AnalyticsIcon, WarningIcon, SettingsIcon, SearchIcon, ClipboardIcon, GoalIcon, LaunchIcon, TimeIcon, TipIcon } from '../../../icons';
import { CompleteAssessment, CAPABILITY_DOMAIN_LABELS, CapabilityDomain } from '../capabilities/types';
import { SelfTestResults } from './testDefinitions';

interface AssessmentSummaryProps {
  completeAssessment: CompleteAssessment;
  selfTestResults?: SelfTestResults | null;
  onClose: () => void;
}

/**
 * AssessmentSummary - Comprehensive overview of all test results
 * 
 * Shows:
 * - DISC Profile (personality)
 * - Workstyle Profile (working preferences)
 * - Extended Skills (domain-by-domain capabilities)
 * - Self Tests (role, knowledge levels)
 * 
 * Purpose: Give user clarity on their complete profile to understand milestone generation
 */
const AssessmentSummary: React.FC<AssessmentSummaryProps> = ({
  completeAssessment,
  selfTestResults,
  onClose,
}) => {
  const { discProfile, workstyleProfile, extendedDecisions } = completeAssessment;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90dvh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 inline-flex items-center gap-1"><AnalyticsIcon size={20} className="inline" /> Jouw Profiel Samenvatting</h2>
            <p className="text-sm text-gray-600 mt-1">
              Alle testresultaten die gebruikt zijn voor je mijlpalen en acties
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 space-y-8">
          {/* 1. DISC Profile */}
          {discProfile && (
            <section className="space-y-3">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                🎨 DISC Persoonlijkheidsprofiel
              </h3>
              <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg p-4 border border-blue-200">
                <div className="flex items-center gap-4 mb-3">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold ${
                    discProfile.primary === 'red' ? 'bg-red-500 text-white' :
                    discProfile.primary === 'yellow' ? 'bg-yellow-400 text-gray-900' :
                    discProfile.primary === 'green' ? 'bg-green-500 text-white' :
                    'bg-blue-500 text-white'
                  }`}>
                    {discProfile.primary === 'red' ? 'D' :
                     discProfile.primary === 'yellow' ? 'I' :
                     discProfile.primary === 'green' ? 'S' : 'C'}
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Primaire stijl</div>
                    <div className="text-xl font-bold text-gray-900">
                      {discProfile.primary === 'red' ? 'Dominant (D)' :
                       discProfile.primary === 'yellow' ? 'Invloed (I)' :
                       discProfile.primary === 'green' ? 'Stabiel (S)' : 'Consciëntieus (C)'}
                    </div>
                  </div>
                </div>
                
                {/* Scores */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-700">🔴 Dominant</span>
                      <span className="font-medium">{discProfile.scores.red}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-red-500 h-2 rounded-full" style={{ width: `${discProfile.scores.red}%` }} />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-700">🟡 Invloed</span>
                      <span className="font-medium">{discProfile.scores.yellow}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-yellow-400 h-2 rounded-full" style={{ width: `${discProfile.scores.yellow}%` }} />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-700">🟢 Stabiel</span>
                      <span className="font-medium">{discProfile.scores.green}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-green-500 h-2 rounded-full" style={{ width: `${discProfile.scores.green}%` }} />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-700">🔵 Consciëntieus</span>
                      <span className="font-medium">{discProfile.scores.blue}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-blue-500 h-2 rounded-full" style={{ width: `${discProfile.scores.blue}%` }} />
                    </div>
                  </div>
                </div>

                {/* Strengths & Weaknesses */}
                {(discProfile.entrepreneurialStrengths.length > 0 || discProfile.entrepreneurialWeaknesses.length > 0) && (
                  <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-blue-200">
                    {discProfile.entrepreneurialStrengths.length > 0 && (
                      <div>
                        <div className="text-xs font-medium text-green-700 mb-1">✓ Sterktes</div>
                        <ul className="text-sm text-gray-700 space-y-0.5">
                          {discProfile.entrepreneurialStrengths.slice(0, 3).map((s: string, i: number) => (
                            <li key={i}>• {s}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {discProfile.entrepreneurialWeaknesses.length > 0 && (
                      <div>
                        <div className="text-xs font-medium text-orange-700 mb-1"><WarningIcon size={14} className="inline" /> Aandachtspunten</div>
                        <ul className="text-sm text-gray-700 space-y-0.5">
                          {discProfile.entrepreneurialWeaknesses.slice(0, 3).map((w: string, i: number) => (
                            <li key={i}>• {w}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </section>
          )}

          {/* 2. Workstyle Profile */}
          {workstyleProfile && (
            <section className="space-y-3">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <SettingsIcon size={16} className="inline" /> Werkstijl Profiel
              </h3>
              <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-lg p-4 border border-orange-200">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-700"><SearchIcon size={14} className="inline" /> Fact Finder</span>
                      <span className="font-medium">{workstyleProfile.scores.fact_finder}/10</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-blue-500 h-2 rounded-full" style={{ width: `${workstyleProfile.scores.fact_finder * 10}%` }} />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-700">⚡ Quick Start</span>
                      <span className="font-medium">{workstyleProfile.scores.quick_start}/10</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-yellow-500 h-2 rounded-full" style={{ width: `${workstyleProfile.scores.quick_start * 10}%` }} />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-700"><ClipboardIcon size={14} className="inline" /> Follow Thru</span>
                      <span className="font-medium">{workstyleProfile.scores.follow_thru}/10</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-green-500 h-2 rounded-full" style={{ width: `${workstyleProfile.scores.follow_thru * 10}%` }} />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-700">🔧 Implementor</span>
                      <span className="font-medium">{workstyleProfile.scores.implementor}/10</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-orange-500 h-2 rounded-full" style={{ width: `${workstyleProfile.scores.implementor * 10}%` }} />
                    </div>
                  </div>
                </div>

                {/* Energy Givers & Ideal Tasks */}
                {(workstyleProfile.energyGivers.length > 0 || workstyleProfile.idealTasks.length > 0) && (
                  <div className="mt-4 pt-4 border-t border-orange-200">
                    <div className="text-xs font-medium text-green-700 mb-2">✓ Jouw werkstijl sterktes</div>
                    <ul className="text-sm text-gray-700 space-y-0.5">
                      {workstyleProfile.energyGivers.slice(0, 3).map((s: string, i: number) => (
                        <li key={i}>• {s}</li>
                      ))}
                      {workstyleProfile.idealTasks.slice(0, 2).map((s: string, i: number) => (
                        <li key={`task-${i}`}>• {s}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* 3. Extended Skills Assessment */}
          {extendedDecisions && extendedDecisions.length > 0 && (
            <section className="space-y-3">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <GoalIcon size={16} className="inline" /> Domein Skills & Uitdagingen
              </h3>
              <div className="space-y-3">
                {extendedDecisions.map((decision) => (
                  <div key={decision.domain} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="font-medium text-gray-900 mb-2">
                      {CAPABILITY_DOMAIN_LABELS[decision.domain as CapabilityDomain] || decision.domain}
                    </div>
                    
                    <div className="space-y-2 text-sm">
                      {/* First Action */}
                      {decision.firstAction && (
                        <div>
                          <span className="text-gray-600 font-medium"><LaunchIcon size={14} className="inline" /> Eerste actie:</span>
                          <span className="text-gray-800 ml-1">{decision.firstAction}</span>
                        </div>
                      )}
                      
                      {/* Missing Knowledge */}
                      {decision.missingKnowledge && decision.missingKnowledge.length > 0 && (
                        <div>
                          <span className="text-gray-600 font-medium">📚 Ontbrekende kennis:</span>
                          <span className="text-gray-800 ml-1">{decision.missingKnowledge.join(', ')}</span>
                        </div>
                      )}
                      
                      {/* Biggest Challenge */}
                      {decision.biggestChallenge && (
                        <div>
                          <span className="text-gray-600 font-medium"><WarningIcon size={14} className="inline" /> Grootste uitdaging:</span>
                          <span className="text-gray-800 ml-1">{decision.biggestChallenge}</span>
                        </div>
                      )}
                      
                      {/* Timeline */}
                      {decision.timeline && (
                        <div>
                          <span className="text-gray-600 font-medium"><TimeIcon size={14} className="inline" /> Tijdlijn:</span>
                          <span className="text-gray-800 ml-1">{decision.timeline}</span>
                        </div>
                      )}
                      
                      {/* Budget */}
                      {decision.budget && (
                        <div>
                          <span className="text-gray-600 font-medium">💰 Budget:</span>
                          <span className="text-gray-800 ml-1">{decision.budget}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* 4. Self Tests (if available) */}
          {selfTestResults && (
            <section className="space-y-3">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                👤 Ondernemersprofiel
              </h3>
              <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-lg p-4 border border-green-200">
                {selfTestResults.rolePreference && (
                  <div className="mb-3">
                    <div className="text-xs font-medium text-gray-600 mb-1">Rolvoorkeur</div>
                    <div className="text-sm text-gray-900">{selfTestResults.rolePreference}</div>
                  </div>
                )}
                {selfTestResults.knowledgeLevels && (
                  <div>
                    <div className="text-xs font-medium text-gray-600 mb-2">Kennisniveaus</div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      {Object.entries(selfTestResults.knowledgeLevels).map(([key, value]) => (
                        <div key={key} className="flex justify-between">
                          <span className="text-gray-700 capitalize">{key}:</span>
                          <span className="font-medium text-gray-900">{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Info footer */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
            <div className="font-medium mb-1"><TipIcon size={14} className="inline" /> Hoe wordt dit gebruikt?</div>
            <p>
              Deze profieldata wordt gebruikt om je mijlpalen en acties zo specifiek mogelijk te maken.
              Bijvoorbeeld: je DISC profiel beïnvloedt welke soort acties je krijgt, en je ontbrekende kennis
              wordt vertaald naar concrete leerdoelen.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors font-medium"
          >
            Sluiten
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssessmentSummary;
